/**
 * Billing Page
 */

import { useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Chip,
  Progress,
  Spinner,
} from '@heroui/react';
import { createFileRoute } from '@tanstack/react-router';
import { ExternalLink, Zap } from 'lucide-react';

import { useSubscription, useUsage, useCreateCheckout } from '@/lib/api/hooks/billing';
import type { PlanType } from '@/api';

export const Route = createFileRoute('/settings/billing')({
  component: BillingPage,
});

interface PlanConfig {
  name: string;
  price: string;
  description: string;
  features: string[];
  recommended?: boolean;
}

const PLANS: Record<'starter' | 'pro', PlanConfig> = {
  starter: {
    name: 'Starter',
    price: '$49',
    description: 'For small teams getting started with audio artifact detection.',
    features: [
      '500 inference minutes/month',
      '10 training jobs/month',
      '5 custom models',
      '$0.12/min overage',
      'Email support',
    ],
  },
  pro: {
    name: 'Pro',
    price: '$199',
    description: 'For growing teams with higher volume needs.',
    features: [
      '2,500 inference minutes/month',
      '50 training jobs/month',
      'Unlimited models',
      '$0.10/min overage',
      'Priority support',
    ],
    recommended: true,
  },
};

function BillingPage() {
  const { data: subscription, isLoading: isLoadingSubscription } = useSubscription();
  const { data: usage, isLoading: isLoadingUsage } = useUsage();
  const createCheckout = useCreateCheckout();
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const handleManageBilling = () => {
    if (subscription?.billing_portal_url) {
      window.location.href = subscription.billing_portal_url;
    }
  };

  const handleUpgrade = async (plan: PlanType) => {
    setCheckoutError(null);
    try {
      const result = await createCheckout.mutateAsync({
        plan,
        success_url: `${window.location.origin}/settings/billing?success=true`,
        cancel_url: `${window.location.origin}/settings/billing?canceled=true`,
      });
      if (result?.checkout_url) {
        window.location.href = result.checkout_url;
      }
    } catch (error) {
      setCheckoutError(
        error instanceof Error
          ? error.message
          : 'Failed to create checkout session. Please try again or contact support.'
      );
    }
  };

  const isLoading = isLoadingSubscription || isLoadingUsage;
  const isTrial = subscription?.plan === 'trial' || (!subscription?.plan && subscription?.status === 'active');
  const isFree = !subscription?.plan || subscription.plan === 'free';
  const isTrialOrFree = isTrial || isFree;

  // Trial limits
  const TRIAL_LIMITS = {
    inference_minutes: 30,
    training_jobs: 1,
  };

  // Use trial limits if on trial, otherwise use API-provided limits
  const effectiveInferenceLimit = isTrial
    ? TRIAL_LIMITS.inference_minutes
    : (usage?.inference_limit ?? 100);
  const effectiveTrainingLimit = isTrial
    ? TRIAL_LIMITS.training_jobs
    : (usage?.training_limit ?? 5);

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="container-page max-w-3xl py-6 animate-fade-in">
      <div className="mb-6">
        <h1>Billing</h1>
        <p className="text-default-500">Manage your subscription and usage</p>
      </div>

      {/* Trial Banner */}
      {isTrial && (
        <div className="mb-6 rounded-xl border border-warning-200 bg-warning-50 p-4">
          <div className="flex items-start gap-3">
            <Zap className="h-5 w-5 text-warning mt-0.5" />
            <div>
              <h3 className="font-medium text-warning-700">You're on a Free Trial</h3>
              <p className="mt-1 text-sm text-warning-600">
                Your trial includes {TRIAL_LIMITS.inference_minutes} inference minutes and{' '}
                {TRIAL_LIMITS.training_jobs} training job. Upgrade to a paid plan for higher limits
                and access to all features.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {/* Current Plan */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <h2>Current Plan</h2>
            <Button
              variant="flat"
              endContent={<ExternalLink className="h-4 w-4" />}
              onPress={handleManageBilling}
              isDisabled={!subscription?.billing_portal_url}
            >
              Manage Billing
            </Button>
          </CardHeader>
          <CardBody className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-default-500">Plan</span>
              <Chip
                color={subscription?.plan === 'trial' ? 'warning' : 'success'}
                variant="flat"
              >
                {subscription?.plan || 'Trial'}
              </Chip>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-default-500">Status</span>
              <Chip
                color={subscription?.status === 'active' ? 'success' : 'default'}
                variant="flat"
              >
                {subscription?.status || 'Active'}
              </Chip>
            </div>
            {subscription?.current_period_end && (
              <div className="flex items-center justify-between">
                <span className="text-default-500">Renewal Date</span>
                <span>
                  {new Date(subscription.current_period_end).toLocaleDateString()}
                </span>
              </div>
            )}
          </CardBody>
        </Card>

        {/* Upgrade Plans - Show for trial/free users */}
        {isTrialOrFree && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-warning" />
                <h2>Upgrade Your Plan</h2>
              </div>
            </CardHeader>
            <CardBody>
              {checkoutError && (
                <div className="mb-4 p-3 rounded-lg bg-danger-50 text-danger text-sm">
                  {checkoutError}
                </div>
              )}
              <div className="flex flex-col gap-4 md:flex-row">
                {(Object.entries(PLANS) as [keyof typeof PLANS, PlanConfig][]).map(([planId, plan]) => (
                  <div
                    key={planId}
                    className={`relative rounded-xl border p-5 ${
                      plan.recommended
                        ? 'border-primary bg-primary-50/30 md:flex-[2]'
                        : 'border-default-200 md:flex-1'
                    }`}
                  >
                    {plan.recommended && (
                      <Chip
                        size="sm"
                        color="primary"
                        variant="flat"
                        className="absolute -top-2.5 right-4"
                      >
                        Recommended
                      </Chip>
                    )}
                    <h3>{plan.name}</h3>
                    <div className="mt-2">
                      <span className="text-3xl font-bold tabular-nums">{plan.price}</span>
                      <span className="text-default-500">/month</span>
                    </div>
                    <p className="mt-2 text-sm text-default-500">{plan.description}</p>
                    <ul className="mt-4 space-y-2">
                      {plan.features.map((feature) => (
                        <li key={feature} className="flex items-start gap-2 text-sm">
                          <span className="text-success">•</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      color={plan.recommended ? 'primary' : 'default'}
                      variant={plan.recommended ? 'solid' : 'bordered'}
                      className="mt-4 w-full"
                      onPress={() => handleUpgrade(planId)}
                      isLoading={createCheckout.isPending}
                    >
                      Choose {plan.name}
                    </Button>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        )}

        {/* Usage */}
        <Card>
          <CardHeader>
            <h2>Usage This Period</h2>
          </CardHeader>
          <CardBody className="space-y-6">
            {/* Inference Minutes */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-default-500">Inference Minutes</span>
                <span className="tabular-nums">
                  {usage?.inference_minutes ?? 0} / {effectiveInferenceLimit}
                  {isTrial && <span className="text-warning ml-1">(Trial)</span>}
                </span>
              </div>
              <Progress
                value={
                  ((usage?.inference_minutes ?? 0) / effectiveInferenceLimit) * 100
                }
                color={isTrial ? 'warning' : 'primary'}
                size="sm"
              />
            </div>

            {/* Training Jobs */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-default-500">Training Jobs</span>
                <span className="tabular-nums">
                  {usage?.training_jobs ?? 0} / {effectiveTrainingLimit}
                  {isTrial && <span className="text-warning ml-1">(Trial)</span>}
                </span>
              </div>
              <Progress
                value={
                  ((usage?.training_jobs ?? 0) / effectiveTrainingLimit) * 100
                }
                color={isTrial ? 'warning' : 'secondary'}
                size="sm"
              />
            </div>

            {/* Storage */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-default-500">Storage</span>
                <span className="tabular-nums">
                  {(usage?.storage_gb ?? 0).toFixed(2)} GB
                </span>
              </div>
              <Progress
                value={Math.min((usage?.storage_gb ?? 0) * 10, 100)}
                color="success"
                size="sm"
              />
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
